'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { getAriaLabel } from '@bridge/utils';
import { HexColorPicker } from 'react-colorful';
import { FlexContainer, Input, Popover, PopoverContent, PopoverTrigger } from '../../components';
import { FieldShell } from './FieldShell';
import { getFieldIds } from './utils';
import type { FieldValues } from 'react-hook-form';
import type { BaseComponentProps, BaseFieldProps } from './types';
const DEFAULT_COLOR = '#FFFFFF';
export type ColorInputComponentProps = BaseComponentProps<typeof Input> & {
  placeholder?: string;
  defaultColor?: string;
};
interface ColorInputFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: ColorInputComponentProps;
}
export function ColorInputField(t0) {
  const $ = _c(51);
  const {
    field,
    fieldConfig,
    fieldState,
    componentProps,
    labelSize
  } = t0;
  let componentDisabled;
  let id;
  let restComponentProps;
  let t1;
  if ($[0] !== componentProps) {
    ({
      id,
      disabled: componentDisabled,
      defaultColor: t1,
      ...restComponentProps
    } = componentProps);
    $[0] = componentProps;
    $[1] = componentDisabled;
    $[2] = id;
    $[3] = restComponentProps;
    $[4] = t1;
  } else {
    componentDisabled = $[1];
    id = $[2];
    restComponentProps = $[3];
    t1 = $[4];
  }
  const defaultColor = t1 === undefined ? DEFAULT_COLOR : t1;
  const isDisabled = field.disabled || componentDisabled;
  let t2;
  if ($[5] !== fieldConfig || $[6] !== fieldState || $[7] !== id) {
    t2 = getFieldIds(fieldConfig, fieldState, id);
    $[5] = fieldConfig;
    $[6] = fieldState;
    $[7] = id;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const ids = t2;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  let t3;
  if ($[9] !== defaultColor || $[10] !== field) {
    t3 = () => {
      if (!field.value) {
        field.onChange(defaultColor);
      }
    };
    $[9] = defaultColor;
    $[10] = field;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  const colorValue = field.value as string || defaultColor;
  let t5;
  if ($[13] !== colorValue) {
    t5 = {
      backgroundColor: colorValue
    };
    $[13] = colorValue;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="sr-only">Pick color</span>;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== isDisabled || $[17] !== t5) {
    t7 = <PopoverTrigger asChild={true}><button className="size-10 rounded border border-border-secondary shrink-0 transition-opacity hover:opacity-80" disabled={isDisabled} style={t5} type="button">{t6}</button></PopoverTrigger>;
    $[16] = isDisabled;
    $[17] = t5;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== field) {
    t8 = color => {
      field.onChange(color);
    };
    $[19] = field;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== colorValue || $[22] !== t8) {
    t9 = <PopoverContent className="w-auto p-3"><HexColorPicker color={colorValue} onChange={t8} /></PopoverContent>;
    $[21] = colorValue;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== isPopoverOpen || $[25] !== t7 || $[26] !== t9) {
    t10 = <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>{t7}{t9}</Popover>;
    $[24] = isPopoverOpen;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const t11 = ids.describedBy;
  const t12 = ids.isInvalid;
  let t13;
  if ($[28] !== fieldConfig.label) {
    t13 = getAriaLabel(fieldConfig.label);
    $[28] = fieldConfig.label;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== field) {
    t14 = e => {
      field.onChange(e.target.value);
    };
    $[30] = field;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsPopoverOpen(true);
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== colorValue || $[34] !== ids.describedBy || $[35] !== ids.fieldId || $[36] !== ids.isInvalid || $[37] !== isDisabled || $[38] !== restComponentProps || $[39] !== t13 || $[40] !== t14) {
    t16 = <Input {...restComponentProps} className="flex-1" aria-describedby={t11} aria-invalid={t12} aria-label={t13} disabled={isDisabled} id={ids.fieldId} onChange={t14} onFocus={t15} value={colorValue} />;
    $[33] = colorValue;
    $[34] = ids.describedBy;
    $[35] = ids.fieldId;
    $[36] = ids.isInvalid;
    $[37] = isDisabled;
    $[38] = restComponentProps;
    $[39] = t13;
    $[40] = t14;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t10 || $[43] !== t16) {
    t17 = <FlexContainer className="gap-2" items="center">{t10}{t16}</FlexContainer>;
    $[42] = t10;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== fieldConfig || $[46] !== fieldState || $[47] !== ids || $[48] !== labelSize || $[49] !== t17) {
    t18 = <FieldShell fieldConfig={fieldConfig} fieldState={fieldState} ids={ids} labelSize={labelSize}>{t17}</FieldShell>;
    $[45] = fieldConfig;
    $[46] = fieldState;
    $[47] = ids;
    $[48] = labelSize;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
}